import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Dropdown,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import { staticConfig, apiConfig } from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const DIRECTION_OPTIONS = [
  { label: "Buy", value: 1 },
  { label: "Sell", value: 2 },
];

const DATE_FORMAT_DISPLAY = "DD MMMM YYYY";

// Converts [2026, 3, 16] → dayjs object for DatePicker
const parseDateArray = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return null;
  const [y, m, d] = arr;
  return dayjs(
    `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
  );
};

// Which action key to disable based on current trade status
const STATUS_TO_DISABLED_ACTION = {
  Booked: "book_trade",
  Confirmed: "confirm_trade",
  Settled: "settle_trade",
  Cancelled: "cancel_trade",
};

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
    </Tooltip>
  </span>
);

const ViewTrade = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [tradeStatus, setTradeStatus] = useState(null);

  // Template dropdown options — same as AddTrade
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [counterpartyOptions, setCounterpartyOptions] = useState([]);
  const [securityOptions, setSecurityOptions] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const tradeId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    fetchTemplate();
  }, []);

  useEffect(() => {
    if (tradeId) fetchTradeDetail(tradeId);
  }, [tradeId]);

  const fetchTemplate = () => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTrades}/trades/template`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load trade template.",
        );
        setIsLoading(false);
        return;
      }

      if (Array.isArray(res?.portfolios)) {
        setPortfolioOptions(
          res.portfolios.map((p) => ({
            label: p?.portfolioName,
            value: p?.portfolioId,
          })),
        );
      }

      if (Array.isArray(res?.counterparties)) {
        setCounterpartyOptions(
          res.counterparties.map((c) => ({
            label: c?.bankName,
            value: c?.id,
          })),
        );
      }

      if (Array.isArray(res?.securities)) {
        setSecurityOptions(
          res.securities.map((s) => ({
            label: s?.instrumentName,
            value: s?.id,
          })),
        );
      }

      setIsLoading(false);
    });
  };

  const fetchTradeDetail = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTrades}/trades/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load trade details.",
        );
        setIsLoading(false);
        return;
      }

      setTradeStatus(res.statusDescription);

      form.setFieldsValue({
        tradeReference: res.tradeReference,
        direction: res.direction ?? null,
        portfolioId: res.portfolioId ?? null,
        counterpartyId: res.counterpartyId ?? null,
        securityId: res.securityId ?? null,
        faceValue: res.faceValue ?? null,
        price: res.price ?? null,
        yield: res.yield ?? null,
        statusDescription: res.statusDescription,
        tradeDate: parseDateArray(res.tradeDate),
        settlementDate: parseDateArray(res.settlementDate),
      });

      setIsLoading(false);
    });
  };

  const runApiAction = (urlSuffix, successMsg,method = "POST",
      urlOverride = null,) => {
    setIsLoading(true);
    const body = { session ,method};
    const url = urlOverride ?? `${apiConfig.GetTrades}/trades/${tradeId}/${urlSuffix}`;

    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Action failed. Please try again.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, successMsg);
      setIsLoading(false);
      fetchTradeDetail(tradeId);
    });
  };

  const handleAction = ({ key }) => {
    switch (key) {
      case "confirm_trade":
        navigate(`/trade/confirm`, { state: tradeId });
        break;
      case "settle_trade":
        runApiAction(null,"Trade settled successfully.","POST", `${apiConfig.GetAuctions}/trades/${tradeId}/settle`);
        break;
      case "cancel_trade":
        navigate(`/trade/cancel`, { state: tradeId });
        break;
      case "convert_allotment_to_trade":
        navigate(`/trade/convert-allotment`, { state: tradeId });
        break;
      default:
        break;
    }
  };

  const disabledKey = STATUS_TO_DISABLED_ACTION[tradeStatus] ?? null;
  const menuItem = (key, label, danger = false) => {
    const isDisabled = key === disabledKey;
    return {
      key,
      disabled: isDisabled,
      label: (
        <span
          style={{
            color: isDisabled ? "#D0D5DD" : danger ? "#B42318" : undefined,
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
        >
          {label}
        </span>
      ),
    };
  };

  const actionMenuItems = [
    menuItem("confirm_trade", "Confirm Trade"),
    menuItem("settle_trade", "Settle Trade"),
    { type: "divider" },
    // menuItem("convert_allotment_to_trade", "Convert Allotment to Trade"),
    menuItem("cancel_trade", "Cancel Trade", true),
  ];

  // Disabled field style — identical to AddTrade's "ds" but read-only
  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };

  return (
    <MainLayout pageItems={staticConfig.viewTrade}>
      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col>
                <h3 style={{ margin: 0, fontWeight: 600 }}>Trade Details</h3>
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>
              {/* ── Trade Reference & Direction ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Trade Reference"
                        tip="The unique reference code for this trade."
                      />
                    }
                    name="tradeReference"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Direction"
                        tip="Whether this is a Buy or Sell trade."
                      />
                    }
                    name="direction"
                  >
                    <Select
                      style={ds}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={DIRECTION_OPTIONS}
                      placeholder="—"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Portfolio & Counterparty ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Portfolio"
                        tip="The portfolio this trade is assigned to."
                      />
                    }
                    name="portfolioId"
                  >
                    <Select
                      style={ds}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={portfolioOptions}
                      placeholder="—"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Counterparty"
                        tip="The counterparty on the other side of this trade."
                      />
                    }
                    name="counterpartyId"
                  >
                    <Select
                      style={ds}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={counterpartyOptions}
                      placeholder="—"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Security ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Security"
                        tip="The underlying security instrument for this trade."
                      />
                    }
                    name="securityId"
                  >
                    <Select
                      style={ds}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={securityOptions}
                      placeholder="—"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Status"
                        tip="The current status of this trade."
                      />
                    }
                    name="statusDescription"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Face Value & Price ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Face Value"
                        tip="The nominal / par value of the instrument being traded."
                      />
                    }
                    name="faceValue"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Price"
                        tip="The price at which the instrument is traded."
                      />
                    }
                    name="price"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={7}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Yield ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Yield (%)"
                        tip="The annualised yield of the instrument at the traded price."
                      />
                    }
                    name="yield"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      precision={5}
                      formatter={(v) => (v ? `${v}%` : "")}
                      parser={(v) => v?.replace(/%/g, "")}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Trade Date"
                        tip="The date on which the trade was executed."
                      />
                    }
                    name="tradeDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format={DATE_FORMAT_DISPLAY}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Trade Date & Settlement Date ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Date"
                        tip="The date on which the trade settles."
                      />
                    }
                    name="settlementDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format={DATE_FORMAT_DISPLAY}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(ViewTrade);
